import {
  createTheme,
  Theme,
  TypographyVariantsOptions as TypographyOptions,
} from "@mui/material/styles";

const defaultTheme: Theme = createTheme();

/**
 * Playful paired Outfit for headings, numbers and labels with Lato for body
 * and UI text. Both are loaded in `index.html`.
 *
 * `Login Reskin.dc.html` — the first mockup drawn natively in Playful — is
 * authoritative for this scale. It confirmed `h1` at 64px was wrong: that was
 * Playful's hero spec, while this app renders `h1` for ordinary page titles
 * ("Welcome back, Rebel!" on the mockup measures 32px). Every variant below
 * was corrected against that mockup where it appears there; where a variant
 * has no example in it, its prior value is kept rather than guessed at (each
 * variant's comment says which applies).
 *
 * Halving `h1` (64→32) left `h2` (30) almost exactly on top of it, so the
 * rest of the heading ramp (h2–h5) is rebalanced to keep each step visually
 * distinct: the mockup gives no evidence for h2–h5 sizes themselves, only for
 * the fact that h1 and h6 both moved, so the values below are chosen to keep
 * roughly even proportional gaps between adjacent steps (~12–25%) rather than
 * scaled by h1's own ratio — h1's *old* size was itself the bug (a hero value
 * doing a page-title's job), so its old proportion to the rest of the ramp
 * isn't a valid scaling reference.
 *
 * Where Playful was silent, the mapping was recorded on the variant itself:
 * Playful defined h1/h2/h3, body, UI labels and captions, whereas this theme
 * carries h1–h6, subtitle1/2, body1/2 and caption. Professional's flip (below)
 * keeps that same fixed variant list — it retargets faces, weights and sizes
 * on it, and retires the three custom roles Playful had added on top.
 */
/**
 * Professional's rule: one type family, weight does the hierarchy. Manrope is
 * the display face and Lato the body face, but unlike Playful — where Outfit
 * appeared only at a single weight (800) — Manrope carries four weights across
 * the roles that use it: 700 (`h1`), 600 (`h2`), 500 (`h3`, `h4`), and 600 on
 * the button label. The weight step *is* the signal that separates adjacent
 * heading levels, not just the size.
 *
 * Manrope stops at `h4`. Below that, `h5`, `h6`, `subtitle1/2`, `body1/2`,
 * `caption` and `overline` are all Lato — at 14px and under, Manrope is
 * visually indistinguishable from Lato at reading sizes, so keeping it there
 * would cost a font-weight download for no visible gain. This is the opposite
 * conclusion from Playful's, which excluded the display face from h3–h5
 * because Outfit never appeared at 19–20px in its guide; here, Manrope
 * appearing at those sizes (on `h3`/`h4`) is the design, and the cutoff moves
 * to `h5` instead.
 */
export const DISPLAY_FONT = "'Manrope', 'Lato', sans-serif";

/**
 * No variant states `textAlign`. Eight of them used to carry
 * `textAlign: "left"` — defensively, against a global `text-align: center` that
 * no longer exists anywhere in `src/theme` or `src/css`. What it does now is
 * break every centred container: a variant's own class outranks the inherited
 * `text-align`, so a `Typography` inside a `textAlign: "center"` panel silently
 * went back to left while its unstyled siblings stayed centred (the AI critique
 * empty state read exactly that way — left title over centred body). Alignment
 * belongs to the box, so the box keeps it.
 */
const typography = {
  fontFamily: "'Lato', 'Roboto', sans-serif",
  /**
   * these typography values can be applied by 'light', 'regular', 'medium' and 'bold'
   * Usage:
   * =====
   *
     <Box sx={{ fontWeight: 'light' }}>
      theme.typography.fontWeightLight
     </Box>

     <Box sx={{ fontWeight: 'regular' }}>
      theme.typography.fontWeightRegular
     </Box>

     <Box sx={{ fontWeight: 'medium' }}>
      theme.typography.fontWeightMedium
     </Box>

     <Box sx={{ fontWeight: 500 }}>
       custom values
     </Box>

     <Box sx={{ fontWeight: 'bold' }}>
        theme.typography.fontWeightMedium
     </Box>
   *
   */
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  /**
   * Page title. 32px, NOT the 64px both Playful's and Professional's theme
   * sheets state — 64 is a hero value and this app renders `h1` for ordinary
   * page titles across 36 call sites. `Login Reskin.dc.html`'s "Welcome back,
   * Rebel!" measures 32.
   *
   * DO NOT "correct" this to 4rem. It has now been wrong in two design sheets
   * in a row; the app's role is what settles it.
   */
  h1: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 700,
    fontSize: defaultTheme.typography.pxToRem(32),
    letterSpacing: "-0.6px",
    lineHeight: 1.1,
  },
  /**
   * Section heading and the hub header band's title. Professional states 30/600
   * with -0.3px tracking; the weight drops from Playful's 800.
   */
  h2: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 600,
    fontSize: defaultTheme.typography.pxToRem(30),
    letterSpacing: "-0.3px",
    lineHeight: 1.2,
  },
  /**
   * Card and in-page section title. This is the single largest change in the
   * Professional flip: 101 call sites move from Lato 19/700 to Manrope 22/500 —
   * bigger, lighter, and on the display face.
   *
   * Playful deliberately kept this on Lato, because its own sheets never drew
   * the display face at 19–22px. Professional's principle is the opposite —
   * "one type family, weight does the hierarchy" — and it measures this role at
   * 22/500 ("Hub Engagement Overview"). It also absorbs the retired
   * `sectionHeading` (24) and `cardTitle` (21) roles, which sat either side of
   * it and are one step in this ramp.
   */
  h3: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 500,
    fontSize: defaultTheme.typography.pxToRem(22),
    letterSpacing: 0,
    lineHeight: 1.3,
  },
  /**
   * Row / UI title — a list row inside a card, a schedule row. Professional's
   * "ui" step, measured at 17/500 ("Builder Schedule"). Absorbs the retired
   * `rowTitle` (16.5).
   */
  h4: {
    fontFamily: DISPLAY_FONT,
    fontWeight: 500,
    fontSize: defaultTheme.typography.pxToRem(17),
    lineHeight: 1.35,
    letterSpacing: 0,
  },
  /**
   * Fifth heading step, Lato 700 — below `h4` the body face takes over, because
   * Manrope at 14px and under is indistinguishable from Lato at reading sizes
   * and costs a font weight to load. Not in the doc; sized one step under `h4`.
   */
  h5: {
    fontWeight: 700,
    fontSize: defaultTheme.typography.pxToRem(14),
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  /**
   * An ordinary small heading, NOT the uppercase eyebrow — it has 37 call sites
   * (Footer, Header, `Tags/Complete`, `BuilderTopicCard`) using it as a plain
   * small heading, and putting the eyebrow's uppercase and tracking on the
   * variant shouted every one of them. The eyebrow is `overline`.
   *
   * The bare-element uppercase rule (`h6:not(.MuiTypography-root)` in
   * `Override.tsx`) only ever applied to bare `<h6>` tags, never to this
   * variant, and is untouched.
   */
  h6: {
    fontWeight: 700,
    fontSize: defaultTheme.typography.pxToRem(13),
    lineHeight: 20 / 13,
    letterSpacing: 0,
  },
  /**
   * Button label. Professional: Manrope 600, and `textTransform: none` stated
   * explicitly rather than MUI's uppercase default.
   */
  button: {
    fontFamily: DISPLAY_FONT,
    textTransform: "none",
    fontWeight: 600,
    fontSize: defaultTheme.typography.pxToRem(15.5),
    letterSpacing: 0,
  },
  /**
   * THE FIELD-LABEL ROLE. Lato 13/700 — Professional's "label" step, and the
   * same value Playful measured. `Field.tsx` and ~16 `InputLabel` call sites
   * render through it (29 total).
   *
   * Professional's theme sheet says Manrope 17/500 for `subtitle1`. Deliberate
   * divergence: that 17px step is `h4` here. Putting it on `subtitle1` would
   * resize every form label in the app.
   */
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(13),
    lineHeight: 20 / 13,
    letterSpacing: 0,
    fontWeight: 700,
  },
  /** Error title. Lato 14/700, measured; otherwise unused. */
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    lineHeight: 20 / 12,
    fontWeight: 700,
    letterSpacing: 0,
  },
  /** Professional body: Lato 15/400. */
  body1: {
    fontSize: defaultTheme.typography.pxToRem(15),
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
  },
  /** Footer / helper text. Colour comes from the `color` prop, never baked in. */
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14.5),
    fontWeight: 400,
    lineHeight: 20 / 13,
    letterSpacing: 0,
  },
  /**
   * Field-level error. Also MUI's `FormHelperText` base style — it spreads
   * `theme.typography.caption` directly (see `FormHelperText.js`) — so every
   * field's validation message renders through this variant.
   */
  caption: {
    display: "inline-block",
    fontSize: defaultTheme.typography.pxToRem(12),
    lineHeight: 18 / 13,
    letterSpacing: 0,
    fontWeight: 700,
  },
  /**
   * The eyebrow. Professional: 11px, weight 500 (down from Playful's 800),
   * 1.4px tracking, uppercase.
   *
   * `textTransform` is on the variant, unlike every other variant here: an
   * eyebrow that is not uppercase is not an eyebrow.
   */
  overline: {
    fontSize: defaultTheme.typography.pxToRem(11),
    fontWeight: 500,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
  },
} as TypographyOptions;

export default typography;
